import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';
import { API_URL_SERVER } from '../../api/api';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';

const AlertPage = () => {
    const { accountId, strategy } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const VALID_SYMBOLS = ['BTCUSD', 'XAUUSD', 'EURUSD', 'USDJPY', 'GBPUSD', 'USTEC', 'USOIL', 'BTCXAU', 'GBPJPY'];

    const isFullAccess = user?.role === 'admin' || (user?.fullAccessAccountIds || []).map(String).includes(String(accountId));

    const [alertData, setAlertData] = useState({
        alertType: 'price', // 'price' or 'time'
        symbol: '',
        alertPrice: '',
        alertTime: '', // Format: 'HH:MM'
        condition: 'reaches',
    });
    const [activeAlerts, setActiveAlerts] = useState([]);
    const [accountSymbols, setAccountSymbols] = useState([]);

    useEffect(() => {
        const fetchAccountDetails = async () => {
            try {
                const response = await axios.get(`${API_URL_SERVER}/accounts`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                const accounts = response.data;
                const account = accounts.find(
                    (acc) => acc.mt5AccountId === accountId && acc.strategy === strategy
                );
                if (account && account.symbols && account.symbols.length > 0) {
                    setAccountSymbols(account.symbols);
                    setAlertData((prev) => ({
                        ...prev,
                        symbol: account.symbols[0],
                    }));
                } else {
                    setAccountSymbols(VALID_SYMBOLS);
                    setAlertData((prev) => ({
                        ...prev,
                        symbol: VALID_SYMBOLS[0],
                    }));
                }
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to fetch account details');
                setAccountSymbols(VALID_SYMBOLS);
                setAlertData((prev) => ({
                    ...prev,
                    symbol: VALID_SYMBOLS[0],
                }));
            }
        };

        fetchAccountDetails();
    }, [accountId, strategy]);

    useEffect(() => {
        const fetchAlerts = async () => {
            try {
                const response = await axios.get(`${API_URL_SERVER}/alerts/${accountId}?strategy=${strategy}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                setActiveAlerts(response.data.alerts || []);
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to fetch alerts');
            }
        };

        fetchAlerts();
    }, [accountId, strategy]);

    const handleAlertChange = (e) => {
        const { name, value } = e.target;
        setAlertData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const validateAlertData = () => {
        if (alertData.alertType === 'price') {
            if (!alertData.symbol) throw new Error('Please select a symbol');
            if (!alertData.alertPrice || isNaN(parseFloat(alertData.alertPrice)) || parseFloat(alertData.alertPrice) <= 0)
                throw new Error('Please enter a valid alert price greater than 0');
        } else if (alertData.alertType === 'time') {
            if (!alertData.alertTime) throw new Error('Please select a valid time');
        }
    };

    const handleSetAlert = async () => {
        try {
            validateAlertData();

            const payload = {
                account_id: accountId,
                strategy,
                alert_type: alertData.alertType,
            };

            if (alertData.alertType === 'price') {
                payload.symbol = alertData.symbol;
                payload.alert_price = parseFloat(alertData.alertPrice);
                payload.condition = alertData.condition;
            } else {
                payload.alert_time = alertData.alertTime;
            }

            const response = await axios.post(`${API_URL_SERVER}/set_alert`, payload, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });

            toast.success(`${alertData.alertType === 'time' ? 'Time' : 'Price'} alert set successfully`);
            setActiveAlerts((prev) => [...prev, { id: response.data.alert_id, ...payload }]);
            
            // Reset form
            setAlertData({
                alertType: 'price',
                symbol: accountSymbols[0] || VALID_SYMBOLS[0],
                alertPrice: '',
                alertTime: '',
                condition: 'reaches',
            });
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to set alert';
            toast.error(errorMessage);
        }
    };

    const handleDisableAlert = async (alertId) => {
        try {
            await axios.post(`${API_URL_SERVER}/disable_alert`, { alert_id: alertId }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });

            toast.success('Alert disabled successfully');
            setActiveAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to disable alert';
            toast.error(errorMessage);
        }
    };

    return (
        <div className="container mt-5">
            <h1 className="text-center">System Alerts - {accountId} ({strategy})</h1>

            <div className="row justify-content-center">
                <div className="col-md-6">
                    <button className="btn btn-secondary mb-3 mt-3" onClick={() => navigate('/')}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-arrow-left" viewBox="0 0 16 16">
                            <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z" />
                        </svg>
                    </button>
                    
                    {isFullAccess && (
                        <div className="card mb-4">
                            <div className="card-body">
                                <h5 className="card-title">Configure Alert</h5>
                                
                                <div className="mb-3">
                                    <label className="form-label">Alert Type</label>
                                    <select className="form-control" name="alertType" value={alertData.alertType} onChange={handleAlertChange}>
                                        <option value="price">Price Alert</option>
                                        <option value="time">Time Alert (IST)</option>
                                    </select>
                                </div>

                                {alertData.alertType === 'price' && (
                                    <>
                                        <div className="mb-3">
                                            <label className="form-label">Symbol</label>
                                            <select className="form-control" name="symbol" value={alertData.symbol} onChange={handleAlertChange}>
                                                {accountSymbols.map((symbol) => (
                                                    <option key={symbol} value={symbol}>{symbol}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Alert Price</label>
                                            <input type="number" step="any" className="form-control" name="alertPrice" value={alertData.alertPrice} onChange={handleAlertChange} placeholder="Enter Alert Price" />
                                        </div>
                                    </>
                                )}

                                {alertData.alertType === 'time' && (
                                    <div className="mb-3">
                                        <label className="form-label">Time (IST)</label>
                                        <input type="time" className="form-control" name="alertTime" value={alertData.alertTime} onChange={handleAlertChange} />
                                        <small className="text-muted">Notification loops every 10 seconds once triggered until disabled.</small>
                                    </div>
                                )}

                                <button className="btn btn-primary" onClick={handleSetAlert}>
                                    Activate Alert
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {activeAlerts.length > 0 && (
                <div className="row justify-content-center mt-4">
                    <div className="col-md-12">
                        <h5 className="mb-3">Active Alerts Engine</h5>
                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>Parameters</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activeAlerts.map((alert) => (
                                        <tr key={alert.id}>
                                            <td className="text-uppercase fw-bold">{alert.alert_type || 'Price'}</td>
                                            <td>
                                                {alert.alert_type === 'time' 
                                                    ? `Target Time: ${alert.alert_time} IST` 
                                                    : `Symbol: ${alert.symbol} | Crosses: ${parseFloat(alert.alert_price).toFixed(5)}`
                                                }
                                            </td>
                                            <td>
                                                {isFullAccess ? (
                                                    <button className="btn btn-warning btn-sm" onClick={() => handleDisableAlert(alert.id)}>
                                                        Terminate
                                                    </button>
                                                ) : (
                                                    <span className="text-muted">Restricted</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AlertPage;